<template>
  <!-- Mobile bottom-sheet (Teleport — DOM-позиція компонента неважлива) -->
  <Teleport to="body">
    <div
      class="md:hidden fixed inset-0 z-[100]"
      :class="open ? 'pointer-events-auto' : 'pointer-events-none'"
    >
      <transition name="sheet-overlay">
        <div
          v-if="open"
          class="absolute inset-0 bg-black/40"
          @click.stop.prevent="close"
          @pointerdown.stop.prevent
        />
      </transition>
      <transition name="sheet">
        <div
          v-if="open"
          class="sheet pointer-events-auto fixed inset-x-0 bottom-0 rounded-t-2xl bg-white p-4 shadow-2xl"
          role="dialog"
          aria-label="Вибір опцій"
          tabindex="-1"
          @keydown.esc.stop.prevent="close"
          @click.stop
          @pointerdown.stop
        >
          <div class="mx-auto max-w-md">
            <div class="flex items-center justify-between mb-3">
              <div class="text-base font-semibold">Опції товару</div>
              <button
                type="button"
                class="inline-flex h-11 w-11 items-center justify-center -m-2"
                aria-label="Закрити"
                @click.stop.prevent="close"
              >
                <X :size="20" />
              </button>
            </div>

            <CatalogProductCardOptionsForm
              shell="sheet"
              :colors="colors"
              :sizes="sizes"
              :color="color"
              :size="size"
              :can-submit="canSubmit"
              @update:color="$emit('update:color', $event)"
              @update:size="$emit('update:size', $event)"
              @confirm="$emit('confirm')"
            />
            <div style="padding-bottom: env(safe-area-inset-bottom)"></div>
          </div>
        </div>
      </transition>
    </div>
  </Teleport>

  <!-- Desktop popover. КОНТРАКТ: компонент має рендеритись усередині
       relative-контейнера картки — позиціонування absolute відносно нього. -->
  <transition name="picker-slide">
    <div
      v-if="open"
      class="hidden md:block absolute inset-x-0 bottom-0 z-20 pointer-events-none px-3 pb-3"
    >
      <div
        ref="panelRef"
        class="rounded-2xl border bg-white/95 p-3 shadow-2xl pointer-events-auto"
        role="dialog"
        aria-label="Вибір опцій"
        tabindex="-1"
        @keydown.esc.stop="close"
        @click.stop.prevent
      >
        <CatalogProductCardOptionsForm
          shell="popover"
          :colors="colors"
          :sizes="sizes"
          :color="color"
          :size="size"
          :can-submit="canSubmit"
          @update:color="$emit('update:color', $event)"
          @update:size="$emit('update:size', $event)"
          @confirm="$emit('confirm')"
        />
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onBeforeUnmount } from "vue";
import { X } from "lucide-vue-next";

/**
 * Пікер опцій картки товару: мобільний bottom-sheet + десктопний popover.
 * Стан вибору живе в картці (v-model:color / v-model:size), відкриття —
 * v-model:open. Outside-click-лістенер вішається на document ТІЛЬКИ поки
 * пікер відкритий (на сторінці каталогу десятки карток — постійний
 * лістенер на кожну був помітним оверхедом).
 */
interface Opt {
  label: string;
  value: string;
}

const props = defineProps<{
  open: boolean;
  colors: Opt[];
  sizes: Opt[];
  color: string;
  size: string;
  canSubmit: boolean;
  /** Кнопка-тогл у картці: її кліки ігноруємо, щоб тогл не "переоткривав" пікер */
  anchorEl?: HTMLElement | null;
}>();

const emit = defineEmits<{
  "update:open": [value: boolean];
  "update:color": [value: string];
  "update:size": [value: string];
  confirm: [];
}>();

const panelRef = ref<HTMLElement | null>(null);

function close() {
  emit("update:open", false);
}

function onDocClick(e: MouseEvent) {
  const t = e.target as Node | null;
  if (!t) return;
  if (panelRef.value?.contains(t)) return;
  if (props.anchorEl?.contains(t)) return;
  close();
}

watch(
  () => props.open,
  (v) => {
    if (!import.meta.client) return;

    if (v) document.addEventListener("click", onDocClick);
    else document.removeEventListener("click", onDocClick);

    // scroll-lock тільки для мобільного sheet
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (isMobile) document.documentElement.style.overflow = v ? "hidden" : "";

    if (v) nextTick(() => panelRef.value?.focus?.());
  },
);

onBeforeUnmount(() => {
  if (!import.meta.client) return;
  document.removeEventListener("click", onDocClick);
  document.documentElement.style.overflow = "";
});
</script>

<style scoped>
.picker-slide-enter-active,
.picker-slide-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.picker-slide-enter-from,
.picker-slide-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
.picker-slide-enter-to,
.picker-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}
.sheet-overlay-enter-active,
.sheet-overlay-leave-active {
  transition: opacity 0.2s ease;
}
.sheet-overlay-enter-from,
.sheet-overlay-leave-to {
  opacity: 0;
}
.sheet-enter-active,
.sheet-leave-active {
  transition:
    transform 0.18s ease,
    opacity 0.18s ease;
}
.sheet-enter-from,
.sheet-leave-to {
  transform: translateY(8%);
  opacity: 0;
}
</style>
